import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { refresh } from '../../services/auth.service';
import Spinner from '../../components/layout/Spinner';
import FadeIn from '../../utils/fadein';
import { FaEllipsisV, FaTimes } from 'react-icons/fa';
import { searchDemos, getCompanyCredits } from '../../utils/api/admin';

function CompanyCredits() {
  const [loaded, setLoaded] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pageBreakdown, setPageBreakdown] = useState([]);
  const [data, setData] = useState([]);
  const [results, setResults] = useState([]);
  const [showSaveBanner, setShowSaveBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [query, setQuery] = useState('');
  const [hideInactive, setHideInactive] = useState(false);
  const [hideNotInSearch, setHideNotInSearch] = useState(false);
  const [hasPurchases, setHasPurchases] = useState(false);
  const imageUrl = process.env.REACT_APP_IMAGE_URL;
  const baseImageUrl = process.env.REACT_APP_BASE_IMAGE_URL;
  const screenshotUrl = process.env.REACT_APP_BASE_IMAGE_URL + 'companyscreenshots/';

  const getDaysAgo = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = Math.abs(today - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getDaysAgoBadgeStyle = (days) => {
    if (days > 60) {
      return "inline-block bg-red-100 text-red-700 text-xs px-2 py-1 rounded-full mt-1";
    }
    return "inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full mt-1";
  };

  useEffect(() => {
    // force build
    async function loadData(){
      let res = await getCompanyCredits();

      if (res.success){
        setData(res.data);
        setLoaded(true);
      }
    }

    loadData();
  }, []);

  const filteredData = data
    .filter(item => !hideInactive || !item.is_inactive)
    .filter(item => !hideNotInSearch || item.is_seller)
    .filter(item => !hasPurchases || item.total_purchases > 0)
    .filter(item => 
      query === '' || 
      item.name.toLowerCase().includes(query.toLowerCase())
    );

  return (
    <>
      <Helmet>
        <title>LeadrPro - Discover new software</title>
      </Helmet>

      <h1>Company Credits {`(${filteredData.length})`}</h1>

      {submitting === false && filteredData.length === 0 && (
        <div className="center mt-20"><strong>Nothing to see here...</strong></div>
      )}

      {submitting === true && (
        <Spinner />
      )}

      {loaded && submitting === false && (
         <div>
          <div className="mb-4 flex items-center gap-4">
            <input
              type="text"
              placeholder="Search by company name or email..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md w-64"
            />

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="hideInactive"
                checked={hideInactive}
                onChange={(e) => setHideInactive(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="hideInactive" className="text-sm text-gray-600">
                Hide inactive companies
              </label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="hideNotInSearch"
                checked={hideNotInSearch}
                onChange={(e) => setHideNotInSearch(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="hideNotInSearch" className="text-sm text-gray-600">
                Hide not in search
              </label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="hasPurchases"
                checked={hasPurchases}
                onChange={(e) => setHasPurchases(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="hasPurchases" className="text-sm text-gray-600">
                Has purchases
              </label>
            </div>
          </div>

          <table className='admindashboard__table table'>
            <thead>
              <tr>
                <th style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10}}></th>
                <th style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10}}>Company ID</th>
                <th style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10}}>Company</th>
                <th style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10}}>Total Purchases</th>
                <th style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10}}>Pending Demos</th>
                <th style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10}}>Remaining Credits</th>
                <th style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10}}>Last Application</th>
                <th style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10}}>Last Approved</th>
                <th style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10}}>Last Viewed</th>
                <th style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10}}>Is Inactive</th>
                <th style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10}}>On Search</th>
              </tr>
            </thead>
            <tbody>
            {filteredData.map((item, index) => (
              <tr key={index}>
                <td>
                  <strong>#{index}.</strong>
                </td>
                <td>
                  {item.company_id}
                </td>
                <td>
                  <div className='flex'>
                    <div>
                      <img src={imageUrl + 'tr:di-@@companylogos@@default-buyer.png/' + item.logo} style={{maxHeight: '24px'}} />
                    </div>

                    <div className='ml-10'>
                      <div className='text-md font-bold'>{item.name}</div>
                      <div>
                        {item.is_featured && (
                          <span className='admindashboard__green-flag'>Featured</span>
                        )}

                        {!item.is_featured && (
                          <span className='admindashboard__red-flag'>Not Featured</span>
                        )}
                      </div>

                      <div>
                        <i>{item.email}</i>
                      </div>
                
                    </div>
                  </div>
                </td>

                <td>
                  {item.total_purchases} / {item.total_purchased_demos} demos
                </td>

                <td>
                  {item.pending_demos || 0}
                </td>

                <td>
                  {item.remaining_credits}
                </td>

                <td>
                  {item.last_application && (
                    <div>
                      <div>{new Date(item.last_application).toDateString()}</div>
                      <div>
                        <span className={getDaysAgoBadgeStyle(getDaysAgo(item.last_application))}>
                          {getDaysAgo(item.last_application)} days ago
                        </span>
                      </div>
                    </div>
                  )}

                  {item.last_application == null && (
                    <span>N/A</span>
                  )}
                </td>

                <td>
                  {item.last_approved && (
                    <div>
                      <div>{new Date(item.last_approved).toDateString()}</div>
                      <div>
                        <span className={getDaysAgoBadgeStyle(getDaysAgo(item.last_approved))}>
                          {getDaysAgo(item.last_approved)} days ago
                        </span>
                      </div>
                    </div>
                  )}

                  {item.last_approved == null && (
                    <span>N/A</span>
                  )}
                </td>

                <td>
                  {item.last_viewed && (
                    <div>
                      <div>{new Date(item.last_viewed).toDateString()}</div>
                      <div>
                        <span className={getDaysAgoBadgeStyle(getDaysAgo(item.last_viewed))}>
                          {getDaysAgo(item.last_viewed)} days ago
                        </span>
                      </div>
                    </div>
                  )}
                  {item.last_viewed == null && (
                    <span>N/A</span>
                  )}
                </td>

                <td>
                  {item.is_inactive == true && (
                    <span className='admindashboard__red-flag'>Inactive</span>
                  )}

                  {item.is_inactive == false && (
                    <span className='admindashboard__green-flag'>Active</span>
                  )}
                </td>

                <td>
                {item.is_seller == true && (
                    <span className='admindashboard__green-flag'>Yes</span>
                  )}

                  {item.is_seller == false && (
                    <span className='admindashboard__red-flag'>No</span>
                  )}
                </td>
              </tr>
            ))}
            </tbody>
          </table>
       </div>
      )}

    {showModal && (
        <>
          <div className='overlay'></div>
          <div className='modal'>
            <div className='modal__close' onClick={() => setShowModal(false)}>
              <FaTimes />
            </div>

            <h2 className='modal__h2'>Admin Note</h2>

            <div className='mt-10'>
            
            </div>
          </div>
        </>
      )}

      {showSaveBanner && (
        <FadeIn delay={150} duration={300}>
          <div className='profileinfo__saved-banner'>
            Profile updated successfully!
          </div>
        </FadeIn>
      )}
    </>
  );
}

export default CompanyCredits;